import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { COLORIZE_LANGUAGE_OPTIONS } from '@/lib/zybooks-api';
import Editor from '@monaco-editor/react';
import { type ReactNodeViewProps } from '@tiptap/react';
import { Trash2Icon } from 'lucide-react';
import { useCallback } from 'react';

const ShortAnswerAnswerWrapperNodeView: React.FC<ReactNodeViewProps> = ({ node, updateAttributes, deleteNode, decorations, editor }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const content = node.attrs.content || '{{ANSWER}}';
    const language = node.attrs.language || 'plaintext';

    const handleContentChange = (value: string | undefined) => {
        const newContent = value || '';
        updateAttributes({ content: newContent });
    };

    const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newLanguage = e.target.value;
        updateAttributes({ language: newLanguage });
    };

    const removeWrapper = useCallback(() => {
        // Check if content has been modified from default
        const hasCustomContent = content !== '{{ANSWER}}';
        if (hasCustomContent) {
            const confirmed = window.confirm('This answer wrapper contains custom content. Are you sure you want to remove it?');
            if (!confirmed) {
                return;
            }
        }
        deleteNode();
    }, [content, deleteNode]);

    const hasAnswerPlaceholder = content.includes('{{ANSWER}}');

    return (
        <ZyNodeViewWrapper
            decorations={decorations}
            className="mt-4 w-full rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-900/20"
        >
            <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-400">Answer Wrapper:</span>
                </div>
                <div
                    className="flex items-center gap-2"
                    contentEditable={false}
                >
                    <label className="text-xs text-gray-600 dark:text-gray-400">Language:</label>
                    <select
                        value={language}
                        onChange={handleLanguageChange}
                        disabled={isEditorReadonly}
                        className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:border-gray-600 dark:bg-gray-900/30 dark:text-gray-200"
                    >
                        {COLORIZE_LANGUAGE_OPTIONS.map(lang => (
                            <option
                                key={lang.value}
                                value={lang.value}
                            >
                                {lang.label}
                            </option>
                        ))}
                    </select>
                    <Button
                        type="button"
                        onClick={removeWrapper}
                        data-style="ghost"
                        aria-label="Remove wrapper"
                        disabled={isEditorReadonly}
                        tooltip="Remove wrapper"
                    >
                        <Trash2Icon className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {!hasAnswerPlaceholder && (
                <div className="mb-2 rounded bg-red-100 p-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400">
                    ⚠️ Warning: Content must include <code className="rounded bg-red-200 px-1 dark:bg-red-900">{'{{ANSWER}}'}</code>{' '}
                    placeholder
                </div>
            )}

            <div className="rounded border border-gray-300 dark:border-gray-600">
                <Editor
                    height="120px"
                    language={language}
                    value={content}
                    onChange={handleContentChange}
                    options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 4,
                        wordWrap: 'on',
                        readOnly: isEditorReadonly,
                    }}
                />
            </div>

            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                💡 Use <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{'{{ANSWER}}'}</code> to mark where the answer should
                appear in the code/text
            </div>
        </ZyNodeViewWrapper>
    );
};

export default ShortAnswerAnswerWrapperNodeView;
